import React, { useEffect } from 'react';
import { Popover, PopoverProps, PopoverState } from 'react-tiny-popover';
import styled from 'styled-components';
import { IoMdClose } from 'react-icons/io';
import { isMobile } from '@/components/responsive';
import Portal from '@/components/portal';

const EMPTY_CLIENT_RECT: ClientRect = {
  height: 0,
  width: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  x: 0,
  y: 0,
  toJSON: () => ({}),
};

const EMPTY_POPOVER_STATE: PopoverState = {
  childRect: EMPTY_CLIENT_RECT,
  popoverRect: EMPTY_CLIENT_RECT,
  parentRect: EMPTY_CLIENT_RECT,
  boundaryRect: EMPTY_CLIENT_RECT,
  position: `bottom`,
  align: `center`,
  padding: 0,
  nudgedLeft: 0,
  nudgedTop: 0,
  boundaryInset: 0,
  violations: EMPTY_CLIENT_RECT,
  hasViolations: false,
};

export function ResponsivePopover(props: PopoverProps) {
  const { children, ...rest } = props;
  const isMobileDevice = isMobile();

  const [shown, setShown] = React.useState(false);

  useEffect(() => {
    setShown(isMobileDevice ? !!rest.isOpen : false);
  }, [rest.isOpen]);

  const closeMobileOverlay = () => {
    setShown(false);
    rest.onClickOutside?.(new MouseEvent(`click`));
  };

  const onBackdropClicked = (ev) => {
    if (ev.target.classList.contains(`vsly-backdrop`)) {
      closeMobileOverlay();
    }
  };

  const renderContent = () => {
    const { content } = rest;
    if (typeof content === `function`) {
      return content(EMPTY_POPOVER_STATE);
    }
    return content;
  };

  return (
    <>
      {shown && (
        <Portal selector="body">
          <Backdrop className="vsly-backdrop" onClick={onBackdropClicked}>
            <MobilePopover>
              <CloseButton onClick={closeMobileOverlay}>
                <IoMdClose size={20} />
              </CloseButton>
              {renderContent()}
            </MobilePopover>
          </Backdrop>
        </Portal>
      )}
      {isMobileDevice ? (
        <div>{children}</div>
      ) : (
        <Popover {...rest} onClickOutside={rest?.onClickOutside}>
          {children}
        </Popover>
      )}
    </>
  );
}

const MobilePopover = styled.div`
  position: fixed;
  top: unset;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: auto;

  && > :nth-child(2) {
    padding: 0 !important;
  }
  .popover-arrow-container {
    padding: 0 !important;
    margin: 0 !important;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  cursor: pointer;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100vw;
  background: rgba(238, 238, 238, 0.76);
  backdrop-filter: blur(4px);
  z-index: 999999999999;
`;

const CloseButton = styled.div`
  position: absolute;
  right: 2rem;
  top: -5rem;
  border-radius: 50%;
  color: #979797;
  background: white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.08);
  height: 3.5rem;
  width: 3.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;
